import {
  AlignmentType,
  BorderStyle,
  Document,
  ImageRun,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from "docx";
import { saveAs } from "file-saver";
import logoIdea20 from "../../assets/images/Logo_IDEA_20.png";

const BORDER = {
  style: BorderStyle.SINGLE,
  size: 8,
  color: "999999",
};

const borders = {
  top: BORDER,
  bottom: BORDER,
  left: BORDER,
  right: BORDER,
};

function pad(n) {
  return String(n).padStart(2, "0");
}

export function buildWordReportFilename(prefix) {
  const now = new Date();
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(
    now.getHours(),
  )}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
  return `${prefix}_${stamp}.docx`;
}

function cellParagraph(text, opts = {}) {
  return new Paragraph({
    alignment: opts.alignment || AlignmentType.CENTER,
    children: [
      new TextRun({
        text: text == null ? "" : String(text),
        bold: !!opts.bold,
        size: opts.size || 20,
      }),
    ],
  });
}

function createHeaderCell(text, width) {
  return new TableCell({
    borders,
    width: { size: width, type: WidthType.DXA },
    children: [cellParagraph(text, { bold: true })],
  });
}

function createDataCell(text, width, opts = {}) {
  return new TableCell({
    borders,
    width: { size: width, type: WidthType.DXA },
    children: [cellParagraph(text, opts)],
  });
}

function createSummaryTable(summary, labels) {
  const colWidth = 2200;
  const headerRow = new TableRow({
    children: [
      createHeaderCell("", colWidth),
      createHeaderCell(labels.hits, colWidth),
      createHeaderCell(labels.errors, colWidth),
      createHeaderCell(labels.omitted, colWidth),
    ],
  });

  const frequency = summary.frequency || {};
  const percentage = summary.percentage || {};

  const frequencyRow = new TableRow({
    children: [
      createDataCell(labels.frequency, colWidth, { bold: true, alignment: AlignmentType.LEFT }),
      createDataCell(frequency.hits, colWidth),
      createDataCell(frequency.errors, colWidth),
      createDataCell(frequency.omitted, colWidth),
    ],
  });

  const percentageRow = new TableRow({
    children: [
      createDataCell(labels.percentage, colWidth, { bold: true, alignment: AlignmentType.LEFT }),
      createDataCell(`${percentage.hits}%`, colWidth),
      createDataCell(`${percentage.errors}%`, colWidth),
      createDataCell(`${percentage.omitted}%`, colWidth),
    ],
  });

  return new Table({
    width: { size: colWidth * 4, type: WidthType.DXA },
    columnWidths: [colWidth, colWidth, colWidth, colWidth],
    rows: [headerRow, frequencyRow, percentageRow],
  });
}

function createBreakdownTable(rows, labels) {
  const nameWidth = 4000;
  const colWidth = 1800;
  const headerRow = new TableRow({
    children: [
      createHeaderCell("", nameWidth),
      createHeaderCell(labels.hits, colWidth),
      createHeaderCell(labels.errors, colWidth),
      createHeaderCell(labels.omitted, colWidth),
    ],
  });

  const dataRows = (rows || []).map(item => {
    const percentage = item.percentage || {};
    return new TableRow({
      children: [
        createDataCell(item.name || "", nameWidth, { alignment: AlignmentType.LEFT }),
        createDataCell(`${percentage.hits}%`, colWidth),
        createDataCell(`${percentage.errors}%`, colWidth),
        createDataCell(`${percentage.omitted}%`, colWidth),
      ],
    });
  });

  return new Table({
    width: { size: nameWidth + colWidth * 3, type: WidthType.DXA },
    columnWidths: [nameWidth, colWidth, colWidth, colWidth],
    rows: [headerRow, ...dataRows],
  });
}

function spacer(after = 120) {
  return new Paragraph({
    spacing: { after },
    children: [],
  });
}

async function loadLogo() {
  try {
    const response = await fetch(logoIdea20);
    if (!response.ok) return null;
    const buffer = await response.arrayBuffer();
    return new Uint8Array(buffer);
  } catch (e) {
    return null;
  }
}

const CHART_MAX_WIDTH = 680;
const CHART_COLUMN_WIDTH = 130;
const CHART_ORANGE = "#F78E00";

function formatCourseWithYear(courseName, year) {
  const course = courseName == null ? "" : String(courseName).trim();
  const yearText = year == null || String(year).trim() === "" ? "" : String(year).trim();
  return [course, yearText].filter(Boolean).join(" ");
}

function formatScoreResult(result, scoreType) {
  if (parseInt(scoreType, 10) === 5) {
    const value = parseFloat(result);
    if (Number.isNaN(value)) return "";
    return value.toFixed(1);
  }
  const value = parseInt(result, 10);
  if (Number.isNaN(value)) return "";
  return String(value);
}

function truncateAssessmentTitle(title) {
  const text = title == null ? "" : String(title);
  if (text.length > 15) return `${text.slice(0, 15)}...`;
  return text;
}

function fitChartLabel(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let value = text;
  while (value.length > 1 && ctx.measureText(`${value}…`).width > maxWidth) {
    value = value.slice(0, -1);
  }
  return `${value}…`;
}

function formatGradePercent(grade) {
  const value = Number(grade);
  if (Number.isNaN(value)) return "0%";
  const rounded = Math.round(value * 10) / 10;
  return `${rounded}%`;
}

function fillRoundTopRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height);
  ctx.beginPath();
  ctx.moveTo(x, y + height);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height);
  ctx.closePath();
  ctx.fill();
}

function canvasToPng(canvas) {
  return new Promise(resolve => {
    canvas.toBlob(async blob => {
      if (!blob) {
        resolve(null);
        return;
      }
      const buffer = await blob.arrayBuffer();
      resolve(new Uint8Array(buffer));
    }, "image/png");
  });
}

/**
 * Vertical bars of achievement percentages, same reading as StudentHistoryDialog.
 * Returns null when there are fewer than two evaluations.
 */
async function renderHistoryChart(history) {
  if (typeof document === "undefined" || !history || history.length < 2) {
    return null;
  }

  const count = history.length;
  const columnWidth = Math.max(1, Math.min(CHART_COLUMN_WIDTH, Math.floor(CHART_MAX_WIDTH / count)));
  const barWidth = Math.min(15, Math.max(4, columnWidth - 8));
  const width = columnWidth * count;
  const valueHeight = 22;
  const chartHeight = 160;
  const labelHeight = 40;
  const height = valueHeight + chartHeight + labelHeight;

  const canvas = document.createElement("canvas");
  const scale = 2;
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.scale(scale, scale);
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, width, height);

  const baseline = valueHeight + chartHeight;
  ctx.strokeStyle = "#EBEBEB";
  ctx.beginPath();
  ctx.moveTo(0, baseline);
  ctx.lineTo(width, baseline);
  ctx.stroke();

  history.forEach((item, index) => {
    const grade = Math.max(0, Math.min(100, Number(item.grade) || 0));
    const barHeight = grade === 0 ? 0 : Math.max(2, (grade / 100) * chartHeight);
    const x = index * columnWidth + (columnWidth - barWidth) / 2;
    const y = baseline - barHeight;
    const center = index * columnWidth + columnWidth / 2;

    ctx.fillStyle = CHART_ORANGE;
    if (barHeight > 0) {
      fillRoundTopRect(ctx, x, y, barWidth, barHeight, 6);
    }

    ctx.font = "12px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText(formatGradePercent(grade), center, Math.max(14, y - 2));

    ctx.font = "11px sans-serif";
    ctx.textBaseline = "top";
    ctx.fillText(
      fitChartLabel(ctx, truncateAssessmentTitle(item.assessmentTitle), columnWidth - 4),
      center,
      baseline + 8,
    );
  });

  const data = await canvasToPng(canvas);
  if (!data) return null;
  return { data, width, height };
}

function buildStudentChildren({
  assessmentName,
  courseName,
  year,
  disciplineName,
  scoreType,
  student,
  labels,
  logoData,
  chart,
  pageBreakBefore,
}) {
  const children = [
    new Paragraph({
      pageBreakBefore: !!pageBreakBefore,
      spacing: { after: 200 },
      children: [
        new TextRun({
          text: `${labels.results} ${assessmentName || ""}`,
          bold: true,
          size: 28,
        }),
        new TextRun({ text: "\t\t" }),
        new TextRun({
          text: formatCourseWithYear(courseName, year),
          bold: true,
          size: 24,
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 200, after: disciplineName ? 80 : 200 },
      children: [
        new TextRun({
          text: student.studentName || "",
          bold: true,
          size: 32,
        }),
      ],
    }),
  ];

  if (disciplineName) {
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
          new TextRun({
            text: disciplineName,
            bold: true,
            size: 28,
          }),
        ],
      }),
    );
  }

  if (student.score && student.score.label) {
    const scoreResult = formatScoreResult(student.score.result, scoreType);
    if (scoreResult !== "") {
      children.push(
        new Paragraph({
          spacing: { before: 80, after: 160 },
          children: [
            new TextRun({
              text: `${student.score.label}: ${scoreResult}`,
              bold: true,
              size: 24,
            }),
          ],
        }),
      );
    }
  }

  children.push(
    createSummaryTable(student.summary || {}, labels),
    spacer(280),
    new Paragraph({
      spacing: { after: 120 },
      children: [
        new TextRun({
          text: labels.achievementByAxis,
          bold: true,
          size: 24,
        }),
      ],
    }),
    createBreakdownTable(student.axis || [], labels),
    spacer(280),
    new Paragraph({
      spacing: { after: 120 },
      children: [
        new TextRun({
          text: labels.achievementBySkill,
          bold: true,
          size: 24,
        }),
      ],
    }),
    createBreakdownTable(student.competency || [], labels),
  );

  if (chart && chart.data) {
    const chartChildren = [spacer(280)];
    if (labels.historyTitle) {
      chartChildren.push(
        new Paragraph({
          spacing: { after: 120 },
          children: [
            new TextRun({
              text: labels.historyTitle,
              bold: true,
              size: 24,
            }),
          ],
        }),
      );
    }
    chartChildren.push(
      new Paragraph({
        children: [
          new ImageRun({
            data: chart.data,
            transformation: { width: chart.width, height: chart.height },
            type: "png",
          }),
        ],
      }),
    );
    children.push(...chartChildren);
  }

  if (logoData) {
    children.push(
      spacer(400),
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new ImageRun({
            data: logoData,
            transformation: { width: 120, height: 48 },
            type: "png",
          }),
        ],
      }),
    );
  }

  return children;
}

/**
 * Build and download a multipage Word report (one page per student).
 */
export async function exportStudentsWordReport({ content, labels, filename }) {
  const students = (content && content.students) || [];
  if (!students.length) {
    throw new Error("NO_STUDENTS");
  }

  const logoData = await loadLogo();
  const charts = [];
  for (let index = 0; index < students.length; index += 1) {
    charts.push(await renderHistoryChart(students[index].history));
  }
  const children = [];

  students.forEach((student, index) => {
    children.push(
      ...buildStudentChildren({
        assessmentName: content.assessmentName,
        courseName: content.courseName,
        year: content.year,
        disciplineName: content.disciplineName,
        scoreType: content.scoreType,
        student,
        labels,
        logoData,
        chart: charts[index],
        pageBreakBefore: index > 0,
      }),
    );
  });

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 720,
              right: 720,
              bottom: 720,
              left: 720,
            },
          },
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, filename || buildWordReportFilename(labels.filenamePrefix || "informe-alumnos"));
}
